import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useIsMobile } from "@/utils/hooks/useIsMobile";
import { CloseIcon } from "@/assets/icons";
import { SpotlightById } from "@/ui/SpotlightById/SpotlightById";
import cn from "classnames";
import styles from "./styles.module.scss";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  spotlightId?: string;
  hideCloseBtn?: boolean;
  children: React.ReactNode;
  wrapperCls?: string;
  overlayCls?: string;
  contentCls?: string;
  ignoreTranslate?: boolean;
  closeOnBackdropClick?: boolean;
  usePortal?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  spotlightId,
  hideCloseBtn,
  children,
  wrapperCls,
  overlayCls,
  contentCls,
  ignoreTranslate,
  closeOnBackdropClick = true,
  usePortal = true,
}) => {
  const isMobile = useIsMobile();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const preventScroll = (e: WheelEvent | TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest(`.${styles.modalContent}`)) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("wheel", preventScroll, { passive: false });
    document.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("wheel", preventScroll);
      document.removeEventListener("touchmove", preventScroll);
    };
  }, [isOpen, onClose]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: isMobile ? 1 : 0.8,
      ...(!ignoreTranslate ? { y: isMobile ? "100%" : 16 } : {}),
    },
    visible: {
      opacity: 1,
      scale: 1,
      ...(!ignoreTranslate ? { y: 0 } : {}),
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
    exit: {
      opacity: 0,
      scale: isMobile ? 1 : 0.8,
      ...(!ignoreTranslate ? { y: isMobile ? "100%" : 16 } : {}),
      transition: { duration: 0.2 },
    },
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick) {
      onClose();
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className={cn(styles.overlayWrapper, overlayCls)}>
          {spotlightId ? (
            <SpotlightById targetId={spotlightId} onClose={onClose} open />
          ) : (
            <motion.div
              className={styles.backdrop}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleBackdropClick}
            />
          )}

          <motion.div
            className={cn(styles.modalWrapper, wrapperCls)}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {!hideCloseBtn && (
              <button
                type="button"
                onClick={onClose}
                className={styles.closeBtn}
              >
                <CloseIcon />
              </button>
            )}
            <div
              className={cn(styles.modalContent, contentCls)}
              ref={contentRef}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return usePortal
    ? ReactDOM.createPortal(modalContent, document.body)
    : modalContent;
};

export default Modal;
